/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {GenericObject} from '@ion/core/src/types/entities';

// props definition for useInitialState hook...
export interface UseInitialStateProps {
  generator?: Function;
  params?: GenericObject;
  value?: any;
};

/**
 * This hook is designed to return the memoized initial state for any useState hook.
 * Some useState state variables require initialization with values that may need
 * to be calculated in potentially costly ways. This calculation is not required
 * on every render of the component. Therefore, to optimize performance,
 * it's necessary to memoize the initial state and return it to the useState hook.
 *
 * ```typescript
 *
 * import { useInitialState } from '@dls/src/hooks/useInitialState';
 *
 * // Simple usage:
 * const [counter, setCounter] = useState(useInitialState({ value: 0 }));
 *
 * // Costly operation for the initial state of columnDefs:
 * const [columnDefs, setColumnDefs] = useState(useInitialState({
 *    generator: getColumnDefinitions,
 *    params: { ...params }
 * }));
 *
 * ```
 * @param generator function that generates the initial state based on provided parameters
 * @param params Parameters to be passed to the generator function.
 * @param value The default value for the state, to be used if no generator function is provided.
 * @returns The memoized initial state.
 */
export const useInitialState = ({
  generator,
  params,
  value
}: UseInitialStateProps) => (
  useMemo(() => (
    generator ? generator(params, value) : value
  ), [])
);

export default useInitialState;
